import { Key, memo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { Autocomplete } from '@ds';
import { AdminFormRow } from '@ui';
import { useInfiniteDropdown } from '@/hooks/useInfiniteDropdown';
import { useInfiniteEmployeeListQuery, useInfiniteManagerListQuery } from '@/queries/employee';
import { renderAutocompleteItem } from '@/utils/helpers';
import { Employee, EmployeeRole } from '@/api/apis/resources';
import { NewHubForm } from '../types';

const managerMapper = (employee: Employee) => ({
  value: employee.id,
  name: employee.firstName && employee.lastName ? `${employee.firstName} ${employee.lastName}`.trim() : '',
});

export type ManagerDropdownProps = {
  isReadOnly?: boolean;
  title: string;
  idFieldName: 'regionalManagerId' | 'nationalManagerId';
  nameFieldName: 'regionalManagerName' | 'nationalManagerName';
  listQuery: typeof useInfiniteEmployeeListQuery | typeof useInfiniteManagerListQuery;
  role: EmployeeRole;
};

export const ManagerDropdown = memo(
  ({ isReadOnly, title, idFieldName, nameFieldName, listQuery, role }: ManagerDropdownProps) => {
    const { control, setValue } = useFormContext<NewHubForm>();
    const initialName = useWatch({ control, name: nameFieldName });

    const { searchValue, list, isLoading, onSearchValue, setAutocompleteOpen, itemsScrollerRef, onItemSelectChange } =
      useInfiniteDropdown({
        setValue: (value: Key | null) => {
          setValue(idFieldName, value?.toString() ?? '');
          const manager = list.find((m) => m.value === value?.toString());
          setValue(nameFieldName, manager ? manager.name : '');
        },
        listQuery,
        mapper: managerMapper,
        initialName,
        defaultSortOrder: { orderBy: 'name', asc: true },
        defaultFilters: { roles: role },
      });

    return (
      <AdminFormRow title={title}>
        <Controller
          name={idFieldName}
          control={control}
          render={({ field: { value }, fieldState: { invalid, error } }) => (
            <Autocomplete
              allowsCustomValue={false}
              placeholder="Choose an option"
              aria-label={title}
              inputValue={searchValue || initialName}
              scrollRef={itemsScrollerRef}
              items={list}
              isLoading={isLoading}
              onInputChange={onSearchValue}
              isInvalid={invalid}
              isDisabled={isReadOnly}
              errorMessage={error?.message}
              onOpenChange={setAutocompleteOpen}
              selectedKey={value || null}
              onSelectionChange={onItemSelectChange}
            >
              {renderAutocompleteItem}
            </Autocomplete>
          )}
        />
      </AdminFormRow>
    );
  },
);
